import { isEmpty, omit, pick } from "lodash";
import { InvoiceStatuses } from "utilities/dist/invoice/constants";
import { computeTaxedTotal, computeSubTotal } from "utilities/dist/invoice/helpers";
import { toNumericPrice } from "../../common/helpers";
import {
    INVOICE_BUILDER_UPDATE_CUSTOMER,
    INVOICE_BUILDER_SET_CUSTOMER,
    INVOICE_BUILDER_SELECT_PRODUCT,
    INVOICE_BUILDER_CHANGE_PRICE,
    INVOICE_BUILDER_CHANGE_QTY,
    INVOICE_BUILDER_TOGGLE_TAXABLE,
    INVOICE_BUILDER_ADD_PRODUCT,
    INVOICE_BUILDER_SAVING,
    INVOICE_BUILDER_SAVED,
    INVOICE_BUILDER_ERROR,
    INVOICE_BUILDER_DUPLICATE_INVOICE,
    INVOICE_BUILDER_REMOVE_ITEM,
    INVOICE_BUILDER_INVOICE_LOADING,
    INVOICE_BUILDER_INVOICE_DATA,
    INVOICE_BUILDER_CHANGE_STATUS,
    INVOICE_BUILDER_CHANGE_DATE,
    INVOICE_BUILDER_CHANGE_STAMP,
    INVOICE_BUILDER_CHANGE_NOTE,
    INVOICE_BUILDER_CHANGE_DELIVERY_TYPE,
    INVOICE_BUILDER_CHANGE_FULFILLMENT_NOTE,
    INVOICE_BUILDER_RESET,
    INVOICE_BUILDER_CLONING_LAST,
    INVOICE_BUILDER_CLONE,
    INVOICE_BUILDER_CREATED,
    INVOICE_BUILDER_SHOW_INLINE_EDITOR,
    INVOICE_BUILDER_HIDE_INLINE_EDITOR,
    INVOICE_BUILDER_CHANGE_INLINE_EDITOR,
    INVOICE_BUILDER_CHANGE_LOGO,
    INVOICE_BUILDER_TOGGLE_LOGO_PICKER,
    INVOICE_CONVERTER_UPLOAD_SUCCESS,
    INVOICE_BUILDER_REMOVE_STAMP_ROLL,
    INVOICE_BUILDER_BEGIN_STAMP_ROLL_EDIT,
    INVOICE_BUILDER_CANCEL_STAMP_FORM,
    INVOICE_BUILDER_ADD_STAMP_ROLL,
    INVOICE_BUILDER_GET_ORDER,
    INVOICE_BUILDER_ADD_PRODUCTS_FROM_RECOMMENDATION,
} from "../constants";
import { buildProductIdKey } from "../../product/helpers";
import { generateReducer, parseCurrency } from "../../common/helpers";
import { isQtyComputable } from "../helpers";

export const initialState = {
    cloningLast: false,
    order_number: "",
    orderNotes: [],
    order_delivery_type: null,

    note: "",
    delivery_type: "pickup",
    fulfillment_note: "",
    items: {},
    editor: {},
    customer: {},
    invoice: null,
    logo_id: null,
    invoiced_at: new Date(),
    status: "",

    inlineEditorFields: [],
    inlineEditorItemId: "",

    stamps: [],
    stampRolls: [],
    availableStamps: [],
    editingStamp: {},
    stampEditRestore: null,
    showLogoPicker: false,

    errors: [],
    duplicateInvoice: null,
    isSaving: false,
    isLoading: false,
};

const addStampRoll = state => {
    const { editingStamp } = state;

    const stampToSave = omit(
        {
            ...editingStamp,
            ...(editingStamp.to ? { to: Number(editingStamp.to) } : {}),
        },
        ["isEditing", "resetStampEditBackup"],
    );

    return {
        ...state,
        editingStamp: {},
        stampEditRestore: null,
        stamps: [...state.stamps, stampToSave],
    };
};

const changeNote = (state, note) => ({ ...state, note });
const changeDeliveryType = (state, delivery_type) => ({ ...state, delivery_type });
const changeFulfillmentNote = (state, fulfillment_note) => ({ ...state, fulfillment_note });
const changeStatus = (state, status) => ({ ...state, status });
const changeLogo = (state, logo_id) => ({ ...state, logo_id });
const removeStampRoll = (state, stampIndex) => {
    const stamps = [...state.stamps];

    stamps.splice(stampIndex, 1);

    return { ...state, stamps };
};

const beginStampRollEdit = (state, stampIndex) => {
    const index = Number(stampIndex);
    const stampBeingEdited = state.stamps[index];
    if (!stampBeingEdited || !Number.isInteger(index)) return state;

    const stamps = [...state.stamps];
    stamps.splice(index, 1);

    return {
        ...state,
        stamps,
        stampEditRestore: { stamp: { ...stampBeingEdited }, index },
    };
};

const cancelStampForm = state => {
    if (!state.stampEditRestore) {
        return { ...state, editingStamp: {}, stampEditRestore: null };
    }

    const { stamp, index } = state.stampEditRestore;
    const stamps = [...state.stamps];
    stamps.splice(index, 0, stamp);

    return {
        ...state,
        stamps,
        editingStamp: {},
        stampEditRestore: null,
    };
};

const changeStamp = (state, stamp = {}) => {
    if (isEmpty(Object.keys(stamp)))
        return { ...state, editingStamp: {}, stampEditRestore: null };

    const resetStampEditBackup = stamp.resetStampEditBackup === true;
    const stampPatch = omit(stamp, ["resetStampEditBackup"]);

    return {
        ...state,
        stampEditRestore: resetStampEditBackup ? null : state.stampEditRestore,
        editingStamp: omit({ ...state.editingStamp, ...stampPatch }, [
            "resetStampEditBackup",
        ]),
    };
};
const setCustomer = (state, customer) => ({ ...state, customer });

const updateInvoiceWithCustomer = (state, customer) => {
    return {
        ...state,
        customer,
        invoice: { ...state.invoice, customer_id: customer.id, customer },
    };
};

const changeDate = (state, invoiced_at) => ({ ...state, invoiced_at });
const selectProduct = (state, { product, taxRate }) => {
    let newState = { ...state, editor: {} };

    if (!isEmpty(product)) {
        newState.editor = {
            product,
            qty: product.qty,
            price: product.price,
            taxable: !!product.taxable,
            total: product.qty * product.price,
            scanned: !!product.scanned,
        };

        newState.editor.total += computeTaxedTotal(newState.editor, taxRate);
    }

    return newState;
};

const changePrice = (state, { price, id, taxRate }) => {
    const newState = { ...state };
    let product = newState.editor;

    if (id) {
        product = { ...newState.products[id] };
    }

    product.price = price;
    product.total = parseCurrency(product.qty * toNumericPrice(product.price));
    product.total += computeTaxedTotal(product, taxRate);

    if (id) {
        newState.products[id] = product;
    }

    return newState;
};

const changeQty = (state, { qty, id, taxRate }) => {
    const newState = { ...state };
    let product = newState.editor;

    if (id) {
        product = { ...newState.products[id] };
    }

    product.qty = isQtyComputable(qty) ? Number(qty) : qty;
    const multipliableQty = isQtyComputable(qty) ? product.qty : 0;
    product.total = computeSubTotal({ price: product.price, qty: multipliableQty });
    product.total += computeTaxedTotal(product, taxRate);

    if (id) {
        newState.products[id] = product;
    }

    return newState;
};
const toggleTaxable = (state, { id, taxRate }) => {
    const newState = { ...state };
    let product = newState.editor;

    if (id) {
        product = { ...newState.products[id] };
    }

    product.taxable = !product.taxable;
    product.total = computeSubTotal(product);
    product.total += computeTaxedTotal(product, taxRate);

    if (id) {
        newState.products[id] = product;
    }

    return newState;
};
const addProduct = state => {
    const item = state.editor;
    const key = buildProductIdKey(item.product);
    const items = { ...state.items, [key]: item };

    return {
        ...state,
        items,
        editor: {},
    };
};

const removeItem = (state, item) => {
    const items = omit(state.items, buildProductIdKey(item.product));

    return { ...state, items };
};

const loadInvoice = (state, invoice) => {
    if (!invoice) {
        return {
            ...state,
            note: "",
            delivery_type: "pickup",
            fulfillment_note: "",
            orderNotes: [],
            order_delivery_type: null,
            items: {},
            stamps: [],
            customer: {},
            logo_id: null,
            invoice: null,
            isLoading: false,
            isSaving: false,
            invoiced_at: new Date(),
            status: InvoiceStatuses.DRAFT,
            errors: [],
            editor: {},
            order_number: "",
            editingStamp: {},
            availableStamps: [],
            stampEditRestore: null,
        };
    }

    // since all items from existing invoice must have an actual product entry
    // we know that the keys can't be source_ so we manually build product_ keys
    const items = {};
    invoice.items.forEach(item => {
        items[`product_${item.product_id}`] = item;
    });
    const orderDeliveryType = invoice.orders?.[0]?.delivery_type ?? null;

    return {
        ...initialState,
        items,
        invoice,
        isLoading: false,

        note: invoice.note,
        delivery_type: invoice.delivery_type ?? orderDeliveryType ?? "pickup",
        fulfillment_note: invoice.fulfillment_note ?? "",
        orderNotes: invoice.orders?.[0]?.note ?? [],
        order_delivery_type: orderDeliveryType,
        stamps: invoice.stamps,
        availableStamps: invoice.stamps,
        status: invoice.status,
        logo_id: invoice.logo_id,
        customer: invoice.customer,
        invoiced_at: invoice.invoiced_at,

        stampRolls: state.stampRolls,
    };
};
const reset = state => ({
    ...initialState,
    // these need to be kept as is since they only load on first page load of the editor
    stampRolls: state.stampRolls,
});
const setSaving = (state, payload) => ({ ...state, isSaving: payload !== false });
const setError = (state, errors) => ({ ...state, errors, isSaving: false });
const setDuplicateInvoice = (state, duplicateInvoice) => ({
    ...state,
    duplicateInvoice,
    isSaving: false,
});
const setInvoiceLoading = (state, isLoading) => ({
    ...state,
    isLoading,
    errors: [],
});

const setShowLogoPicker = (state, showLogoPicker) => ({
    ...state,
    showLogoPicker,
});

const setCloning = (state, cloningLast) => ({ ...state, cloningLast });
const setOrderInvoice = (state, orderInvoice) => ({ ...state, orderInvoice });

const cloneLast = (state, invoice) => {
    const orderDeliveryType =
        invoice?.order_delivery_type ?? invoice?.orders?.[0]?.delivery_type ?? null;
    const newState = {
        ...state,
        stamps: [],
        stampEditRestore: null,
        items: {},
        order_number: invoice?.order_number,
        order_delivery_type: orderDeliveryType,
        delivery_type: invoice?.delivery_type ?? orderDeliveryType ?? "pickup",
        fulfillment_note: invoice?.fulfillment_note ?? "",
        orderNotes: Array.isArray(invoice?.orderNotes) ? invoice.orderNotes : [],
        ...pick(invoice, ["note", "customer"]),
    };
    invoice.items.forEach(item => {
        newState.items[buildProductIdKey(item.product, item.ydk_product)] = item;
    });

    return newState;
};

const addItemsFromRecommendation = (state, items = []) => {
    const newState = {
        ...state,
        stamps: [],
        stampEditRestore: null,
    };
    items.forEach(product => {
        newState.items[buildProductIdKey(product, product.ydk_product)] = {
            product,
            qty: product.qty,
            price: product.price,
            taxable: !!product.taxable,
            total: product.qty * product.price,
            scanned: !!product.scanned,
        };
    });

    return newState;
};

const showInlineEditor = (state, { item, inlineEditorFields }) => {
    return {
        ...state,
        inlineEditorFields,
        inlineEditorItemId: buildProductIdKey(item.product, item.ydk_product),
    };
};

const hideInlineEditor = (state, field) => {
    const newState = {
        ...state,
        inlineEditorFields: state.inlineEditorFields.filter(f => f !== field),
    };

    if (newState.inlineEditorFields.length < 1) {
        newState.inlineEditorItemId = "";
    }

    return newState;
};

const updateItemInline = (state, { field, value, taxRate }) => {
    if (state.inlineEditorFields.length < 1) return state;

    const newState = { ...state },
        item = newState.items[state.inlineEditorItemId];
    if (field === "price") {
        item.price = value ?? "";
    } else if (field === "qty") {
        item.qty = isQtyComputable(value) ? Number(value) : value;
    }

    item.total = computeSubTotal({
        price: toNumericPrice(item.price),
        qty: isQtyComputable(item.qty) ? item.qty : 0,
    });
    item.total += computeTaxedTotal(item, taxRate);

    newState.items[state.inlineEditorItemId] = item;

    return newState;
};

const addItemsFromConvertedFile = (state, { items }) => {
    const newState = { ...state, items: {}, stamps: [], stampEditRestore: null };

    items.forEach(item => {
        newState.items[buildProductIdKey(item.product)] = item;
    });

    return newState;
};

export const reducers = {
    [INVOICE_BUILDER_SET_CUSTOMER]: setCustomer,
    [INVOICE_BUILDER_UPDATE_CUSTOMER]: updateInvoiceWithCustomer,

    [INVOICE_BUILDER_CHANGE_QTY]: changeQty,
    [INVOICE_BUILDER_ADD_PRODUCT]: addProduct,
    [INVOICE_BUILDER_CHANGE_DATE]: changeDate,
    [INVOICE_BUILDER_CHANGE_NOTE]: changeNote,
    [INVOICE_BUILDER_CHANGE_DELIVERY_TYPE]: changeDeliveryType,
    [INVOICE_BUILDER_CHANGE_FULFILLMENT_NOTE]: changeFulfillmentNote,
    [INVOICE_BUILDER_CHANGE_LOGO]: changeLogo,
    [INVOICE_BUILDER_CHANGE_PRICE]: changePrice,
    [INVOICE_BUILDER_CHANGE_STATUS]: changeStatus,
    [INVOICE_BUILDER_TOGGLE_TAXABLE]: toggleTaxable,
    [INVOICE_BUILDER_SELECT_PRODUCT]: selectProduct,

    [INVOICE_BUILDER_CHANGE_STAMP]: changeStamp,
    [INVOICE_BUILDER_ADD_STAMP_ROLL]: addStampRoll,
    [INVOICE_BUILDER_REMOVE_STAMP_ROLL]: removeStampRoll,
    [INVOICE_BUILDER_BEGIN_STAMP_ROLL_EDIT]: beginStampRollEdit,
    [INVOICE_BUILDER_CANCEL_STAMP_FORM]: cancelStampForm,

    [INVOICE_BUILDER_REMOVE_ITEM]: removeItem,

    [INVOICE_BUILDER_TOGGLE_LOGO_PICKER]: setShowLogoPicker,
    [INVOICE_BUILDER_CLONING_LAST]: setCloning,
    [INVOICE_BUILDER_CLONE]: cloneLast,

    [INVOICE_BUILDER_SHOW_INLINE_EDITOR]: showInlineEditor,
    [INVOICE_BUILDER_HIDE_INLINE_EDITOR]: hideInlineEditor,
    [INVOICE_BUILDER_CHANGE_INLINE_EDITOR]: updateItemInline,

    [INVOICE_BUILDER_RESET]: reset,
    [INVOICE_BUILDER_SAVED]: reset,
    [INVOICE_BUILDER_CREATED]: reset,
    [INVOICE_BUILDER_ERROR]: setError,
    [INVOICE_BUILDER_DUPLICATE_INVOICE]: setDuplicateInvoice,
    [INVOICE_BUILDER_SAVING]: setSaving,

    [INVOICE_BUILDER_GET_ORDER]: setOrderInvoice,

    [INVOICE_BUILDER_INVOICE_DATA]: loadInvoice,
    [INVOICE_BUILDER_INVOICE_LOADING]: setInvoiceLoading,

    [INVOICE_CONVERTER_UPLOAD_SUCCESS]: addItemsFromConvertedFile,
    [INVOICE_BUILDER_ADD_PRODUCTS_FROM_RECOMMENDATION]: addItemsFromRecommendation,

};

export default generateReducer(reducers, initialState);
